import { Injectable } from "@angular/core";
import { UIAnnotation } from "@features/labeling/models/annotation";
import { UILabel } from "@features/labeling/models/label";
import { AnnotationFactory } from "@features/labeling/services/annotation.factory";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { cloneDeep } from "lodash";
import { combineLatest, Observable, of, ReplaySubject, Subject } from "rxjs";
import { distinctUntilChanged, map, shareReplay, switchMap, withLatestFrom } from "rxjs/operators";
import { ColorMeaning, LabelingColorService } from "../../services/labeling-color.service";
import { LabelingReviewAnnotationGroupService } from "./labeling-review-annotation-group.service";
import { LabelingReviewService, ReviewStatus } from "./labeling-review.service";

@UntilDestroy()
@Injectable()
export class LabelingReviewUpdateService {
    currentReview$ = new ReplaySubject<UILabel>(1);
    fetchedReview$ = new ReplaySubject<UILabel>(1);
    updatingReview$: Observable<boolean>;
    resetReviewTrigger$ = new Subject<void>();
    isDirty$: Observable<boolean>;
    reviewStatus$: Observable<ReviewStatus>;
    canEditAnnotations$: Observable<boolean>;

    constructor(
        private labelingReviewService: LabelingReviewService, 
        private labelingColorService: LabelingColorService,
        private annotationFactory: AnnotationFactory,
        private labelingReviewAnnotationGroupService: LabelingReviewAnnotationGroupService
    ) {
        this.labelingReviewService.currentReviewRecord$.pipe(
            untilDestroyed(this)
        ).subscribe((record) => {
            if (record) {
                const answer = record.verifiedAnswer || null;
                const review = this.annotationFactory.fromAnswer(answer);
                this.currentReview$.next(review);
                this.fetchedReview$.next(cloneDeep(review));
                if (answer !== null) {
                    this.labelingColorService.setCurrentColorMeaning(ColorMeaning.CLASS);
                }
            }
        });

        this.updatingReview$ = this.labelingReviewService.currentReviewRecord$.pipe(
            map((reviewRecord) => {
                return reviewRecord.status === ReviewStatus.REJECTED || reviewRecord.status === ReviewStatus.VALIDATED;
            }),
            shareReplay()
        );

        this.reviewStatus$ = combineLatest([this.updatingReview$, this.labelingReviewService.currentReviewRecord$]).pipe(
            switchMap(([updatingReview, reviewRecord]) => {
                if (updatingReview) {
                    // if updating the review, it cannot be in conflict or in consensus
                    return of(reviewRecord.status);
                }

                return this.labelingReviewAnnotationGroupService.isConflicting$.pipe(
                    map(isConflicting => isConflicting ? ReviewStatus.CONFLICTING : ReviewStatus.CONSENSUS)
                )
            })
        );

        this.canEditAnnotations$ = this.reviewStatus$.pipe(
            map((status) => status !== ReviewStatus.REJECTED)
        );

        this.isDirty$ = combineLatest([
            this.currentReview$,
            this.fetchedReview$
        ]).pipe(
            distinctUntilChanged(),
            map(([annotation, fetchedAnnotation]) => {
                return !annotation.equals(fetchedAnnotation);
            }),
        );
        
        this.resetReviewTrigger$.pipe(
            untilDestroyed(this),
            withLatestFrom(this.fetchedReview$)
        ).subscribe(([_trigger, fetchedReview]) => {
            this.currentReview$.next(cloneDeep(fetchedReview));
        })
    }

    updateReview(annotations: UIAnnotation[]) {
        this.currentReview$.next(this.annotationFactory.fromAnnotations([...annotations]));
    }

    resetReview() {
        this.resetReviewTrigger$.next();
    }
}